import { EngagementKeys } from '../../common/engagementKeys';
import {
  loadLivePersonAssets,
  loadCookieGatedCoreAssets,
  loadBrandAssets,
  loadChatUiAssets
} from '../../utils/asset-loader-utils';

import { LiveChatError } from '../../utils/common-utils';

function isExcludedRoute(currentPath: string, excludedRoutes: string[]): boolean {
  return excludedRoutes.some((pattern) => {
    const regexPattern = pattern.replace(/\*/g, '[^ ]*');
    const regex = new RegExp(regexPattern);
    return regex.test(currentPath);
  });
}

export async function loadUiResources(
  manifest: Manifest,
  resources: Resources,
  bucketPath: string
) {
  if (resources.ui?.enabled) {
    const excludedRoutes = resources.ui.excluded_routes || [];
    const globalExclusions = ['*/checkout*', '*/cart*', '*/iframe*'];
    excludedRoutes.push(...globalExclusions);

    const currentPath = window.location.pathname;

    if (!isExcludedRoute(currentPath, excludedRoutes)) {
      // Check if container already exists to prevent duplicates
      let liveChatContainer = document.getElementById('live-chat-ui-container');
      if (!liveChatContainer) {
        // Create the containing div
        liveChatContainer = document.createElement('div');
        liveChatContainer.id = 'live-chat-ui-container';
        document.body.appendChild(liveChatContainer);
      }

      let liveChatEngagements = document.getElementById('live-chat-engagements');
      if (!liveChatEngagements) {
        liveChatEngagements = document.createElement('div');
        liveChatEngagements.id = 'live-chat-engagements';
        liveChatContainer.appendChild(liveChatEngagements);
      }

      // Create a div for each engagementKey; at runtime, the LivePerson Web Taglet will fill each with a LivePerson HTML Engagement
      Object.keys(EngagementKeys).forEach((engKey: string) => {
        if (!document.getElementById(engKey)) {
          const div = document.createElement('div');
          div.id = engKey;
          liveChatEngagements.appendChild(div);
        }
      });

      // Check if UI assets have already been loaded to prevent duplicate loading
      if (!window.LiveChat?.uiAssetsLoaded) {
        await loadChatUiAssets(manifest.ui, bucketPath);
        // @ts-expect-error - Unpacking window.LiveChat leads TS to expect possible undefined vars. These are verified upstream, however, so we will ignore this check.
        window.LiveChat = {
          ...window.LiveChat,
          uiAssetsLoaded: true
        };
      }
    }
  }
}

function isCookiesAllowed(): boolean {
  return !!window.OptanonActiveGroups && window.OptanonActiveGroups.indexOf('3') !== -1;
}

export async function loadLivePersonResources(
  manifest: Manifest,
  resources: Resources,
  bucketPath: string
) {
  if (resources.liveperson && resources.liveperson.enabled) {
    await loadLivePersonAssets(manifest.liveperson, bucketPath);
    loadCookieGatedCoreAssets(manifest.asset_loader, bucketPath);
  }

  // Register a listener for changes to allowed cookies, and load the LP script if it wasn't loaded before
  window.addEventListener('OneTrustGroupsUpdated', async () => {
    if (
      isCookiesAllowed() &&
      resources!.liveperson.enabled &&
      !(window.lpTag?._tagCount && window.lpTag?._tagCount > 0)
    ) {
      await loadLivePersonAssets(manifest?.liveperson, bucketPath);
    }
  });
}

export async function fetchAndLoadBrandAssets(
  resources: Resources,
  api: LiveChatApi,
  bucketPath: string
) {
  const currentBrand = window.LiveChat!.brandCode;
  const environment = window.LiveChat!.environment;

  if (!api.useConfigSystem && !api.brand_assets_manifest_filename) {
    throw new Error('brand_assets_manifest_filename is missing.');
  }

  const response = api.useConfigSystem
    ? await fetch(`/livechat/${environment}/manifests/${currentBrand}-manifest.json`)
    : await fetch(`${bucketPath}/brands/${api.brand_assets_manifest_filename}`);

  if (response.ok) {
    const brandAssetsManifest = await response.json();
    await loadBrandAssets(resources, brandAssetsManifest, bucketPath);
  }
}

async function assetLoaderInit() {
  const environment = window.LiveChat?.environment;
  const bucketPath = window.LiveChat?.useConfigSystem ? `/livechat/${environment}` : '/livechat';
  const api: LiveChatApi | undefined = window.LiveChat;
  const manifest = window.LiveChat?.manifest || null;
  const resources = window.LiveChat?.resources;

  if (!api || !manifest || !resources) {
    throw new LiveChatError(
      'There are files missing from the window.LiveChat object. Cannot load LiveChat.'
    );
  }

  await loadUiResources(manifest, resources, bucketPath);
  await loadLivePersonResources(manifest, resources, bucketPath);
  await fetchAndLoadBrandAssets(resources, api, bucketPath);
}

if (typeof window !== 'undefined' && window.LiveChat) {
  assetLoaderInit();
}
